import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "semantic-ui-react";
import { Addons, HotelAddonInventory, Hotels } from "../../../api/services/next";
import EditEntityPopup from '../../../components/shared/EditEntityPopup';
import { onError, onSuccess } from "../../../components/shared/messages";
import MultiPair from "../../../components/shared/MultiPair";
import useBoolean from "../../../hooks/useBoolean";
import { useCurrentHotel } from "../../../hooks/useCurrentHotel";
import { useTextResources } from '../../../language';

const defaultHotelAddonInventory = {
    IsInventoryManaged: false
}

const AddMultiHotelAddonInventory = ({
    open,
    onClose,
}) => {
    const text = useTextResources();
    // const message = useMessageResources();
    const isCreate = true;
    const {
        value: isLoading,
        setTrue: setLoading,
        setFalse: resetLoading
    } = useBoolean(false);

    const [groups, setGroups] = useState([]);

    const onChange = useCallback((value) => {

        const { hotels, addons } = value || {};

        if (!hotels || !addons) {
            setGroups([])
            return;
        }

        const result = [];

        for (let hotelId in hotels) {
            if (hotels[hotelId]) {
                for (let addonId in addons) {
                    if (addons[addonId]) {
                        result.push({
                            ...defaultHotelAddonInventory,
                            HotelId: parseInt(hotelId),
                            ChainAddonId: parseInt(addonId),
                        });
                    }
                }
            }
        }

        setGroups(result);
    }, []);

    const handleSave = useCallback(async () => {
        try {
            setLoading();
            await HotelAddonInventory.SaveHotelAddonInventory(groups);
            onSuccess();
            return true;
        } catch (error) {
            console.log(error);
            onError(error)
            return false;
        } finally {
            resetLoading();
        }
    }, [groups, resetLoading, setLoading]);

    const handleClose = useCallback(() => {
        if (onClose) onClose();
        setGroups([])
    }, [onClose])

    const handleSaveAndClose = useCallback(async () => {
        const isSaved = await handleSave();
        if (isSaved) {
            handleClose();
        }
    }, [handleSave, handleClose]);

    return (
        <EditEntityPopup
            open={open}
            isCreate={isCreate}
            header={text("AddMultiHotelAddonInventory")}
            onClose={handleClose}
            onSave={handleSave}
            onSaveAndClose={handleSaveAndClose}
            isLoading={isLoading}
        >
            <Form>
                <Form.Field
                    control={GroupLists}
                    onChange={onChange}
                />
            </Form>
        </EditEntityPopup>
    );
};

AddMultiHotelAddonInventory.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

AddMultiHotelAddonInventory.defaultProps = {
    open: false,
    onClose: () => { },
}

export default AddMultiHotelAddonInventory;


const GroupLists = ({
    onChange
}) => {
    const [addons, setAddons] = useState([]);
    const [hotels, setHotels] = useState([]);
    const currentHotel = useCurrentHotel();

    const loadAddons = useCallback(async () => {
        const addons = await Addons.GetHotelChainAddons();
        setAddons(addons);
    }, []);

    const loadHotels = useCallback(async () => {
        const hotels = await Hotels.GetHotels({
            HotelChainId: currentHotel?.HotelChainId
        });
        setHotels(hotels);
    }, [currentHotel]);

    useEffect(() => {
        loadAddons();
        loadHotels();
    }, [loadAddons, loadHotels])

    const addonSearchSelector = useCallback((addon, searchTerm) =>
        addon.Name?.toLowerCase().includes(searchTerm?.toLowerCase()), []);

    const hotelSearchSelector = useCallback((hotel, searchTerm) =>
        hotel.Name?.toLowerCase().includes(searchTerm?.toLowerCase()), []);

    const addonItemRender = useCallback((addon) => {
        const name = [];

        if (addon.Name) {
            name.push(addon.Name)
        }

        return name.join('-')
    }, []);

    const hotelItemRender = useCallback((hotel) => {
        const name = []

        if (hotel.Name) {
            name.push(hotel.Name)
        }

        return name.join('-')
    }, []);

    const addonItemKeySelector = useCallback((addon) => {
        return addon.Id
    }, [])

    const hotelItemKeySelector = useCallback((hotel) => {
        return hotel.Id
    }, [])

    const itemsLists = useMemo(() => {
        return [
            {
                name: "addons",
                listLabel: "Addons",
                hasSearch: true,
                searchSelector: addonSearchSelector,
                itemRender: addonItemRender,
                itemKeySelector: addonItemKeySelector,
                items: addons,
            },
            {
                name: "hotels",
                listLabel: "Hotels",
                hasSearch: true,
                searchSelector: hotelSearchSelector,
                itemRender: hotelItemRender,
                itemKeySelector: hotelItemKeySelector,
                items: hotels,
            },

        ]
    }, [addons, hotels])

    return (
        <MultiPair
            itemsLists={itemsLists}
            hasSelectAll
            onChange={onChange}
        />
    );
};

GroupLists.propTypes = {
    onChange: PropTypes.func.isRequired,
};